
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { useBookmark } from "@/hooks/useBookmark";
import { useParams, Link, useNavigate } from "@/lib/router-compat";
import { getRouteApi } from "@tanstack/react-router";
import Seo, { SITE_URL } from "@/components/seo/Seo";
import { authorSlug, publisherJsonLd } from "@/lib/newsSeo";
import { articlePath, shareImage, isUuid } from "@/lib/articleUrl";
import { ChevronLeft, BookmarkIcon, Share2, MessageCircle, Play, Pause, ExternalLink, Loader2, Globe } from "lucide-react";
import { motion } from "framer-motion";
import Navbar from "@/components/layout/Navbar";
import ThemeLogo from "@/components/ui/ThemeLogo";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import ArticleCard from "@/components/ui/ArticleCard";
import CommentSection from "@/components/comments/CommentSection";
import AIWidgetsSidebar from "@/components/ai/AIWidgetsSidebar";
import FloatingAIButton from "@/components/ai/FloatingAIButton";
import SentimentWidget from "@/components/ai/SentimentWidget";
import { LanguageSelector } from "@/components/article/LanguageSelector";
import ArticleFaq from "@/components/article/ArticleFaq";
import type { ArticleFaqItem } from "@/lib/articleFaq.functions";

import { useArticleAI } from "@/hooks/useArticleAI";
import { useCleanArticleContent } from "@/hooks/useCleanArticleContent";
import { fetchArticleWithFallback } from "@/services/unifiedNewsService";
import { NewsArticle } from "@/types/news";
import { useNews } from "@/context/NewsContext";
import { toast } from "sonner";
import { useArticleAudioPlayer } from "@/context/ArticleAudioPlayerContext";
import { stripHtml } from "@/lib/utils";
import { scrapeArticle } from '@/lib/articleAnalysis.functions';
import { supabase } from "@/integrations/supabase/client";
import { translateArticle, translateArticleForSite, rewriteArticle, getSavedArticleTranslation } from "@/lib/articleAi.functions";
import { TRANSLATION_CODE } from "@/i18n/config";
import { useLocale } from "@/i18n/useLocale";
import { getCachedPreparedStory, loadPreparedStory } from "@/lib/preparedStory";
import ShareArticleModal from "@/components/article/ShareArticleModal";
import SmartImage from "@/components/ui/SmartImage";
import { proxyHtmlImages } from "@/lib/images";
import { cleanArticleHtml } from "@/lib/articleContent";
import { sanitizeArticleHtml } from "@/lib/sanitizeHtml";
import { isInternalSource } from "@/lib/internalSource";
import { T } from "@/i18n/T";


const DEFAULT_CATEGORY = "Technology";
const STANDARD_CATEGORIES = [
  "Bitcoin", "Ethereum", "DeFi", "NFTs", "Layer 2", 
  "Regulation", "Altcoins", "Web3", "Security", "Exchanges"
];

const articleRouteApi = getRouteApi("/_app/article/$id");

const Article = () => {
  const { id } = useParams<{ id: string }>();
  // The stored translation comes with the page from the server, so the reader
  // never sees English first.
  const preloaded = articleRouteApi.useLoaderData() as
    | { translation: { title: string; excerpt: string; content: string | null; audioUrl: string | null } | null }
    | null;
  const navigate = useNavigate();
  const [article, setArticle] = useState<NewsArticle | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { allCachedArticles } = useNews();
  const { playArticle, pause, currentArticle, isPlaying: isAudioPlaying } = useArticleAudioPlayer();
  const [isLoadingAudio, setIsLoadingAudio] = useState(false);
  const locale = useLocale();
  // The site language decides which translation a reader gets by default.
  const [selectedLanguage, setSelectedLanguage] = useState(TRANSLATION_CODE[locale] ?? 'en');
  const [translatedArticle, setTranslatedArticle] = useState<{title: string, excerpt: string, content: string | null, audioUrl: string | null} | null>(
    preloaded?.translation ?? null,
  );
  // On a translated edition, start in the "waiting" state so English text is
  // never painted before the saved translation is in place.
  const [isTranslating, setIsTranslating] = useState(
    (TRANSLATION_CODE[locale] ?? 'en') !== 'en' && !preloaded?.translation,
  );
  const [articleCache, setArticleCache] = useState<Record<string, NewsArticle>>({});
  // Which address the currently held article was loaded for (prevents redirecting back to the previous story)
  const [loadedFor, setLoadedFor] = useState<string | null>(null);
  
  useEffect(() => {
    const loadArticle = async () => {
      if (!id) {
        setError("No article ID provided");
        setIsLoading(false);
        return;
      }
      
      // Check cache first for instant load
      if (articleCache[id]) {
        setArticle(articleCache[id]);
        setLoadedFor(id);
        setError(null);
        setIsLoading(false);
        return;
      }
      
      setIsLoading(true);
      setError(null);
      
      try {
        const fetchedArticle = await fetchArticleWithFallback(id);
        
        if (!fetchedArticle) {
          setError("Article not found");
          setArticle(null);
          
          toast.error("Article not found", {
            description: "This article may not have been synced yet. Try refreshing the page or check back later.",
            action: {
              label: "Refresh",
              onClick: () => window.location.reload()
            }
          });
        } else {
          setArticle(fetchedArticle);
          setLoadedFor(id);
          setError(null);
          // Cache the article for instant subsequent loads
          setArticleCache(prev => ({ ...prev, [id]: fetchedArticle }));
        }
      } catch (error) {
        console.error("Failed to fetch article:", error);
        setError("Failed to load article");
        setArticle(null);
        
        toast.error("Failed to load article", {
          description: "There was an error loading the article. Please try again.",
          action: {
            label: "Retry",
            onClick: () => window.location.reload()
          }
        });
      } finally {
        setIsLoading(false);
      }
    };
    
    loadArticle();
    window.scrollTo(0, 0);
  }, [id, articleCache]);

  // Swap a legacy address (UUID or an old slug) for the readable one, before paint when possible
  useLayoutEffect(() => {
    if (!id) return;
    const known = allCachedArticles.find((a) => a.id === id || a.slug === id);
    if (known?.slug && known.slug !== id) {
      navigate(`/article/${known.slug}`, { replace: true });
    }
  }, [id, allCachedArticles, navigate]);

  useEffect(() => {
    // Only rewrite the address once the story held in state was loaded for this exact address,
    // otherwise a newly opened story would bounce back to the previous one.
    if (loadedFor && loadedFor === id && article?.slug && id !== article.slug) {
      navigate(`/article/${article.slug}`, { replace: true });
    }
  }, [article?.slug, id, loadedFor, navigate]);

  
  
  // Readers on /no or /zh-hant get the stored translation straight from the database.
  // Nothing is translated while the page is open: if no saved version exists yet,
  // the English text stays and the background job fills it in shortly after publishing.
  const autoTranslatedFor = useRef<string | null>(null);
  useEffect(() => {
    if (!article) return;
    if (selectedLanguage === 'en') return;
    if (selectedLanguage !== 'no' && selectedLanguage !== 'zh-Hant') return;
    const key = `${article.id}:${selectedLanguage}`;
    if (autoTranslatedFor.current === key) return;
    autoTranslatedFor.current = key;

    // Already delivered with the page — nothing to fetch.
    if (preloaded?.translation && translatedArticle) {
      setIsTranslating(false);
      return;
    }

    let cancelled = false;
    setIsTranslating(true);
    void (async () => {
      try {
        const saved = await getSavedArticleTranslation({
          data: { articleId: article.id, language: selectedLanguage },
        });
        if (cancelled) return;
        if (saved?.translation) setTranslatedArticle(saved.translation);
      } catch {
        /* keep the English text */
      }
      if (!cancelled) setIsTranslating(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [article?.id, selectedLanguage]);


  const relatedArticles = allCachedArticles
    .filter(a => a.category === article?.category && a.id !== article?.id)
    .slice(0, 3);

  const handleLanguageChange = async (
    languageCode: string,
    options?: { silent?: boolean },
  ) => {
    if (!article) return;
    const silent = options?.silent === true;

    setSelectedLanguage(languageCode);

    // If switching back to English, clear translation
    if (languageCode === 'en') {
      setTranslatedArticle(null);
      return;
    }

    setIsTranslating(true);

    try {
      const isSiteLanguage = languageCode === 'no' || languageCode === 'zh-Hant';
      const translateFn = isSiteLanguage ? translateArticleForSite : translateArticle;
      const data = await translateFn({
        data: {
          articleId: article.id,
          languageCode,
          title: article.title,
          excerpt: article.excerpt ?? '',
          content: article.content || article.excerpt,
        }
      });

      if (data?.success && data?.translation) {
        setTranslatedArticle(data.translation);
      } else {
        throw new Error('Failed to translate article');
      }
    } catch (error: any) {
      console.error("Failed to translate article:", error);

      if (silent) {
        // Automatic translation for the site language: keep the English text in place.
        setTranslatedArticle(null);
      } else if (error?.message?.includes('rate limit') || error?.message?.includes('Too Many Requests')) {
        toast.error("Translation rate limit reached", {
          description: "Please wait a moment before translating again.",
          duration: 5000,
        });
        setSelectedLanguage('en');
      } else {
        toast.error("Translation failed", {
          description: error?.message || "Could not translate article. Please try again."
        });
        setSelectedLanguage('en');
      }
    } finally {
      setIsTranslating(false);
    }
  };

  const handleReadArticle = async () => {
    if (!article) return;
    
    const currentLanguage = selectedLanguage;
    
    // Check if this is the current playing article
    const isCurrentArticle = currentArticle?.id === article.id;
    
    // Priority 1: Use stored audio URL (from translation or original)
    const storedAudioUrl = currentLanguage === 'en' ? article.audio_url : translatedArticle?.audioUrl;
    if (storedAudioUrl) {
      console.log("Using stored audio:", storedAudioUrl);
      playArticle({
        id: article.id,
        title: translatedArticle?.title || article.title,
        source: article.source.name,
        audioUrl: storedAudioUrl,
        imageUrl: article.imageUrl,
      });
      return;
    }

    // Priority 2: Generate audio, store it, and play
    console.log("No stored audio found, generating and storing...");
    
    try {
      setIsLoadingAudio(true);
      toast.info("Generating audio...", {
        id: "generating-audio",
      });
      
      // Call edge function to generate and store audio
      const { data, error } = await supabase.functions.invoke('generate-article-audio', {
        body: {
          articleId: article.id,
          regenerate: false,
          languageCode: currentLanguage
        }
      });

      if (error) throw error;

      if (data?.audioUrl) {
        console.log("Audio generated and stored:", data.audioUrl);
        
        // Update local state with new audio URL
        if (currentLanguage === 'en') {
          setArticle({ ...article, audio_url: data.audioUrl });
        } else if (translatedArticle) {
          setTranslatedArticle({ ...translatedArticle, audioUrl: data.audioUrl });
        }
        
        // Play the audio
        playArticle({
          id: article.id,
          title: translatedArticle?.title || article.title,
          source: article.source.name,
          audioUrl: data.audioUrl,
          imageUrl: article.imageUrl,
        });
        
        toast.dismiss("generating-audio");
        toast.success("Audio ready!", {
          description: "Playing audio now"
        });
      } else {
        throw new Error("No audio URL returned");
      }
      
    } catch (error) {
      console.error("Failed to generate audio:", error);
      toast.dismiss("generating-audio");
      toast.error("Failed to generate audio", {
        description: "Please try again later"
      });
    } finally {
      setIsLoadingAudio(false);
    }
  };

  const getArticleContent = () => {
    const contentElement = document.querySelector('.prose');
    if (!contentElement) return article?.excerpt || '';
    
    const paragraphs = Array.from(contentElement.querySelectorAll('p, h2'))
      .slice(1)
      .map(p => p.textContent)
      .filter(Boolean)
      .join('. ');
      
    return paragraphs;
  };

  return (
    <div className="min-h-screen flex flex-col">
      <main className="flex-1 pt-8">
        <div className="container mx-auto px-2 md:px-4 py-4 md:py-8">
          {isLoading ? (
            <ArticleSkeleton />
          ) : error ? (
            <ArticleNotFound error={error} />
          ) : article ? (
            <ArticleContentWithAI 
              article={article}
              translatedArticle={translatedArticle}
              selectedLanguage={selectedLanguage}
              onLanguageChange={handleLanguageChange}
              isTranslating={isTranslating}
              relatedArticles={relatedArticles}
              handleReadArticle={handleReadArticle}
              handlePauseArticle={pause}
              isPlaying={isAudioPlaying && currentArticle?.id === article.id}
              isLoadingAudio={isLoadingAudio}
            />
          ) : (
            <ArticleNotFound error="Article not found" />
          )}
        </div>
      </main>
    </div>
  );
};

const ArticleNotFound = ({ error }: { error: string }) => (
  <div className="text-center py-16">
    <h2 className="text-2xl font-bold mb-4"><T k="auto.article-not-found-39d5" /></h2>
    <p className="text-muted-foreground mb-8">
      {error === "Article not found" 
        ? "The article you're looking for doesn't exist or hasn't been synced yet. Try running a manual sync or check back later." 
        : "There was an error loading the article. Please try again later."
      }
    </p>
    <div className="flex gap-4 justify-center">
      <Button asChild>
        <Link to="/"><T k="auto.return-to-home-7d70" /></Link>
      </Button>
      <Button variant="outline" onClick={() => window.location.reload()}>
        <T k="auto.try-again-d876" />
      </Button>
      <Button variant="outline" asChild>
        <Link to="/admin"><T k="auto.admin-panel-9371" /></Link>
      </Button>
    </div>
  </div>
);

const ArticleContentWithAI = ({ 
  article,
  translatedArticle,
  selectedLanguage,
  onLanguageChange,
  isTranslating,
  relatedArticles,
  handleReadArticle,
  handlePauseArticle,
  isPlaying,
  isLoadingAudio
}: {
  article: NewsArticle;
  translatedArticle: {title: string, excerpt: string, content: string | null, audioUrl: string | null} | null;
  selectedLanguage: string;
  onLanguageChange: (lang: string) => void;
  isTranslating: boolean;
  relatedArticles: NewsArticle[];
  handleReadArticle: () => void;
  handlePauseArticle: () => void;
  isPlaying: boolean;
  isLoadingAudio: boolean;
}) => {
  const { isBookmarked, toggle: toggleBookmark } = useBookmark(article?.id);
  const [showShareModal, setShowShareModal] = useState(false);
  const {
    sentimentData,
    tldrData,
    loadingSentiment,
    loadingTldr,
    autoAnalyzeArticle,
  } = useArticleAI();

  // Universal AI article cleaner (cache-first, one pass per article)
  const aiCleanedContent = useCleanArticleContent(article?.id, article?.content, article?.contentCleaned);

  // On-demand full-text fetch when we only stored the RSS blurb
  const [fetchedContent, setFetchedContent] = useState<string | null>(null);
  const [isFetchingFullText, setIsFetchingFullText] = useState(false);
  const [fullTextFailed, setFullTextFailed] = useState(false);
  const [rewrittenContent, setRewrittenContent] = useState<string | null>(null);
  const [isRewriting, setIsRewriting] = useState(false);
  const [rewriteFailed, setRewriteFailed] = useState(false);
  const requestArticleRewrite = useServerFn(rewriteArticle);
  const [faqItems, setFaqItems] = useState<ArticleFaqItem[]>([]);

  const fullTextAttemptedFor = useRef<string | null>(null);
  const rewriteAttemptedFor = useRef<string | null>(null);

  useEffect(() => {
    const ready = getCachedPreparedStory(article?.id);
    setFetchedContent(null);
    setFullTextFailed(false);
    setRewrittenContent(ready ?? null);
    setIsRewriting(false);
    setRewriteFailed(false);
  }, [article?.id]);

  // Background jobs prepare most stories ahead of time, so check that first.
  const [preparedChecked, setPreparedChecked] = useState(false);

  useEffect(() => {
    const articleId = article?.id;
    if (!articleId) return;
    let cancelled = false;
    setPreparedChecked(false);
    void loadPreparedStory(articleId).then((ready) => {
      if (cancelled) return;
      if (ready) {
        setRewrittenContent(ready);
        rewriteAttemptedFor.current = articleId;
        fullTextAttemptedFor.current = articleId;
      }
      setPreparedChecked(true);
    });
    return () => {
      cancelled = true;
    };
  }, [article?.id]);

  useEffect(() => {
    const articleId = article?.id;
    const needsFullText =
      !!articleId &&
      preparedChecked &&
      !rewrittenContent &&
      !isInternalSource(article?.source?.id) &&
      (article?.content || '').trim().length < 600;
    if (!needsFullText || fullTextAttemptedFor.current === articleId) return;


    let cancelled = false;

    // Full-text fetching needs a signed-in reader.
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (cancelled) return;
      fullTextAttemptedFor.current = articleId;
      if (!session) {
        setFullTextFailed(true);
        return;
      }
      setIsFetchingFullText(true);

      scrapeArticle({
        data: { articleId: articleId!, sourceName: article?.source?.name ?? '' },
      })
        .then((data) => {
          if (cancelled) return;
          const content = data?.content;
          if (data?.success && content && content.length > 600) {
            setFetchedContent(content);
          } else {
            setFullTextFailed(true);
          }
        })
        .catch(() => {
          if (!cancelled) setFullTextFailed(true);
        })
        .finally(() => {
          if (!cancelled) setIsFetchingFullText(false);
        });
    });

    return () => {
      cancelled = true;
    };
  }, [article?.id, preparedChecked, rewrittenContent]);

  // When only the wire blurb is stored, show a Byteline write-up instead
  useEffect(() => {
    const articleId = article?.id;
    const needsRewrite =
      !!articleId &&
      fullTextFailed &&
      (article?.content || '').trim().length < 600 &&
      !fetchedContent;
    if (!needsRewrite) return;
    if (rewriteAttemptedFor.current === articleId) return;

    let cancelled = false;
    setIsRewriting(true);
    setRewriteFailed(false);

    const loadRewrite = async () => {
      for (let attempt = 0; attempt < 2; attempt += 1) {
        try {
          const res = await requestArticleRewrite({ data: { articleId } });
          if (cancelled) return;
          rewriteAttemptedFor.current = articleId;
          if (res?.success && res.content && res.content.length > 400) {
            setRewrittenContent(res.content);
            setIsRewriting(false);
            return;
          }
        } catch (error) {
          if (!cancelled) rewriteAttemptedFor.current = articleId;
          if (attempt === 1) console.error("Article rewrite request failed", error);
        }
      }

      // The server route can be unavailable (missing server config). Any
      // write-up already saved can still be read straight from the database.
      try {
        const { data: saved } = await supabase
          .from('article_analysis')
          .select('analysis_data')
          .eq('article_id', articleId!)
          .eq('analysis_type', 'rewrite')
          .maybeSingle();
        const savedContent = (saved?.analysis_data as { content?: string } | null)?.content;
        if (!cancelled && typeof savedContent === 'string' && savedContent.length > 400) {
          setRewrittenContent(savedContent);
          setIsRewriting(false);
          return;
        }
      } catch {
        // fall through to the source card
      }

      if (!cancelled) {
        setRewriteFailed(true);
        setIsRewriting(false);
      }
    };

    void loadRewrite();

    return () => {
      cancelled = true;
    };
  }, [article?.id, article?.content, fetchedContent, fullTextFailed]);



  useEffect(() => {
    if (article?.id) {
      autoAnalyzeArticle(article.id, selectedLanguage);
    }
  }, [article?.id, selectedLanguage, autoAnalyzeArticle]);

  const contentRef = useRef<HTMLDivElement | null>(null);


  const formattedDate = new Date(article.publishedAt).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
  
  const normalizeCategory = (category: string): string => {
    if (!category) return DEFAULT_CATEGORY;
    
    const matchedCategory = STANDARD_CATEGORIES.find(
      sc => sc.toLowerCase() === category.toLowerCase()
    );
    
    if (matchedCategory) return matchedCategory;
    
    for (const stdCat of STANDARD_CATEGORIES) {
      if (category.toLowerCase().includes(stdCat.toLowerCase())) {
        return stdCat;
      }
    }
    
    if (category.toLowerCase().includes('btc')) return 'Bitcoin';
    if (category.toLowerCase().includes('eth')) return 'Ethereum';
    if (category.toLowerCase().includes('dao')) return 'DeFi';
    if (category.toLowerCase().includes('nft')) return 'NFTs';
    
    return category.charAt(0).toUpperCase() + category.slice(1);
  };
  
  const displayCategory = normalizeCategory(article.category ?? '');
  
  const processArticleContent = (content: string, articleTitle?: string): string => {
    if (!content) return '';

    // Deep clean: remove links, widgets/embeds, publisher furniture and duplicates
    content = cleanArticleHtml(content, articleTitle);

    
    // Normalize Next.js optimized image URLs in both src and srcset, and add lazy-loading + fallbacks
    const decodeNextImageUrl = (url: string) => {
      try {
        const match = url.match(/url=([^&]+)/);
        if (match?.[1]) return decodeURIComponent(match[1]);
        return url;
      } catch {
        return url;
      }
    };

    // 1) Replace src/data-src that point to Next.js optimizer
    let processedContent = content.replace(
      /(src|data-src)="([^\"]*\/_next\/image\?[^\"]+)"/g,
      (_m, attr, url) => `${attr}="${decodeNextImageUrl(String(url))}` + `"`
    );

    // 2) Replace srcset/data-srcset entries that include Next.js optimizer
    processedContent = processedContent.replace(
      /(srcset|data-srcset)="([^"]+)"/g,
      (_m, attr, value) => {
        const rebuilt = String(value)
          .split(',')
          .map((part: string) => {
            const [u, size] = part.trim().split(/\s+/);
            const newU = u?.includes('/_next/image') ? decodeNextImageUrl(u) : u;
            return [newU, size].filter(Boolean).join(' ');
          })
          .join(', ');
        return `${attr}="${rebuilt}"`;
      }
    );

    // 3) Route body images through the resizing proxy (WebP, max 800px wide)
    processedContent = proxyHtmlImages(processedContent, 800);

    // 3b) Add lazy loading, referrerPolicy and error fallback to all <img loading="lazy" decoding="async"> tags if missing
    processedContent = processedContent
      .replace(/<img(?![^ loading="lazy" decoding="async">]*loading=)/g, '<img loading="lazy"')
      .replace(/<img(?![^>]*referrerpolicy=)/g, '<img referrerpolicy="no-referrer"')
      .replace(/<img(?![^ loading="lazy" decoding="async">]*onerror=)/g, "<img onerror=\"this.onerror=null;this.src='/placeholder.svg';\"");
    
    // 4) Remove problematic grid/layout CSS classes from scraped content
    const problematicClassPattern = /\b(grid|grid-cols-\d+|md:grid-cols-\d+|lg:grid-cols-\d+|xl:grid-cols-\d+|gap-\d+|md:gap-\d+|lg:gap-\d+|pt-\d+|md:pt-\d+|lg:pt-\d+|row-start-\d+|col-start-\d+|col-span-\d+|row-span-\d+|items-stretch|items-start|items-center|items-end|justify-items-\w+)\b/g;
    
    processedContent = processedContent.replace(
      /class="([^"]*)"/g,
      (match, classes) => {
        const cleanedClasses = classes
          .split(/\s+/)
          .filter((cls: string) => !problematicClassPattern.test(cls))
          .join(' ')
          .trim();
        return cleanedClasses ? `class="${cleanedClasses}"` : '';
      }
    );
    
    // If content has HTML structure (AI-formatted), return processed content
    if (processedContent.includes('<p>') || processedContent.includes('<div>')) {
      return processedContent;
    }
    
    // Fallback for unformatted content (should be rare after AI formatting)
    return processedContent
      .split('\n\n')
      .filter(para => para.trim().length > 50)
      .map(para => `<p>${para.trim()}</p>`)
      .join('\n');
  };
  const createArticleContent = () => {
    const ours = isInternalSource(article.source?.id);
    const displayContent =
      translatedArticle?.content || fetchedContent || aiCleanedContent || article.content;
    const displayExcerpt = translatedArticle?.excerpt || article.excerpt;
    
    if (!displayContent || displayContent.trim().length < 200) {
      if (rewrittenContent) {
        return (
          <div className="space-y-6">
            {!ours && (
              <div className="rounded-lg border border-border bg-muted/30 px-4 py-3 text-sm text-muted-foreground">
                <T k="article.rewrittenPrefix" />{' '}
                <a
                  href={article.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-medium text-foreground underline underline-offset-2"
                >
                  {article.source.name}
                </a>{' '}
                <T k="article.rewrittenSuffix" />
              </div>
            )}

            <div
              ref={contentRef}
              className="article-content prose prose-lg dark:prose-invert max-w-none"
              dangerouslySetInnerHTML={{ __html: sanitizeArticleHtml(rewrittenContent) }}
            />

            {!ours && (
              <div className="flex justify-center pt-2">
                <a
                  href={article.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 bg-byteline-green text-byteline-dark px-6 py-3 rounded-lg hover:bg-byteline-green/90 transition-colors font-medium"
                >
                  Read the original on {article.source.name} <ExternalLink className="h-4 w-4" />
                </a>
              </div>
            )}
          </div>
        );
      }

      return (
        <div className="space-y-6">
          <div className="article-excerpt text-lg leading-relaxed">
            {stripHtml(displayExcerpt)}
          </div>

          {!ours && (isFetchingFullText || isRewriting || !rewriteFailed) ? (
            <div className="space-y-3" aria-live="polite">
              <div className="flex items-center gap-2 text-sm text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" />
                {isRewriting ? 'Writing this story…' : 'Loading the full story…'}
              </div>
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-11/12" />
              <Skeleton className="h-4 w-10/12" />
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-9/12" />
            </div>
          ) : ours ? null : (
            <div className="rounded-lg border border-border bg-muted/30 p-6">
              <p className="text-center text-base mb-4">
                <T k="auto.this-story-couldn-t-be-loaded-in-full-he-2027" />
              </p>
              <div className="flex justify-center">
                <a 
                  href={article.url} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 bg-byteline-green text-byteline-dark px-6 py-3 rounded-lg hover:bg-byteline-green/90 transition-colors font-medium"
                >
                  Open on {article.source.name} <ExternalLink className="h-4 w-4" />
                </a>
              </div>
            </div>
          )}
        </div>
      );
    }

    // Waiting for the reader's language: show a placeholder instead of English text.
    if (isTranslating && selectedLanguage !== 'en' && !translatedArticle) {
      return (
        <div className="space-y-6">
          <div className="flex items-center gap-2 text-sm text-muted-foreground bg-muted/30 px-3 py-2 rounded-lg">
            <Globe className="h-4 w-4 animate-pulse" />
            <span><T k="common.translating" /></span>
          </div>
          <div className="space-y-3">
            {Array.from({ length: 10 }).map((_, i) => (
              <div
                key={i}
                className="h-4 rounded bg-muted animate-pulse"
                style={{ width: `${[100, 96, 92, 98, 88, 100, 94, 90, 97, 70][i]}%` }}
              />
            ))}
          </div>
        </div>
      );
    }

    return (
      <div className="space-y-6">
        {selectedLanguage !== 'en' && (
          <div className="flex items-center gap-2 text-sm text-muted-foreground bg-muted/30 px-3 py-2 rounded-lg">
            <Globe className="h-4 w-4" />
            <span><T k="auto.this-article-has-been-translated-677d" /></span>
          </div>
        )}
        {displayExcerpt && (
          <div 
            className="article-excerpt text-lg leading-relaxed font-medium text-muted-foreground"
            dir={selectedLanguage === 'ar' ? 'rtl' : 'ltr'}
          >
            {stripHtml(displayExcerpt)}
          </div>
        )}
        <div 
          ref={contentRef}
          className="article-content prose prose-lg dark:prose-invert max-w-none"
          dir={selectedLanguage === 'ar' ? 'rtl' : 'ltr'}
          dangerouslySetInnerHTML={{ __html: sanitizeArticleHtml(processArticleContent(displayContent, article?.title)) }}
        />

      </div>
    );
  };
  
  const canonicalPath = articlePath(article);
  const ogImage = shareImage(article.imageUrl);
  const seoDescription = stripHtml(translatedArticle?.excerpt || article.excerpt || article.title || '');
  const bylineName = article.author?.name || article.source?.name || 'Byteline';
  const bylineSlug = authorSlug(bylineName);
  const isOurStory = isInternalSource(article.source?.id);

  return (
    <>
      <Seo
        title={translatedArticle?.title || article.title}
        description={seoDescription}
        path={canonicalPath}
        image={ogImage}
        type="article"
        publishedTime={article.publishedAt}
        author={article.author?.name || article.source?.name}
        section={displayCategory}
        modifiedTime={article.updatedAt || article.publishedAt}
        jsonLd={[
          {
            "@context": "https://schema.org",
            "@type": "NewsArticle",
            headline: String(article.title || "").slice(0, 110),
            description: seoDescription,
            image: ogImage
              ? [{ "@type": "ImageObject", url: ogImage, width: 1200, height: 630 }]
              : undefined,
            datePublished: article.publishedAt,
            dateModified: article.updatedAt || article.publishedAt,
            articleSection: displayCategory,
            inLanguage: selectedLanguage || "en",
            isAccessibleForFree: true,
            keywords: article.topicTags?.length ? article.topicTags.join(", ") : undefined,
            mainEntityOfPage: {
              "@type": "WebPage",
              "@id": `${SITE_URL}${canonicalPath}`,
            },
            author: {
              "@type": "Person",
              name: bylineName,
              url: `${SITE_URL}/authors/${bylineSlug}`,
            },
            publisher: publisherJsonLd,
          },
          {
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "Home", item: `${SITE_URL}/` },
              { "@type": "ListItem", position: 2, name: displayCategory, item: `${SITE_URL}/feed` },
              { "@type": "ListItem", position: 3, name: article.title, item: `${SITE_URL}${canonicalPath}` },
            ],
          },
          ...(faqItems.length === 2
            ? [
                {
                  "@context": "https://schema.org",
                  "@type": "FAQPage",
                  mainEntity: faqItems.map((item) => ({
                    "@type": "Question",
                    name: item.question,
                    acceptedAnswer: { "@type": "Answer", text: item.answer },
                  })),
                },
              ]
            : []),
        ]}
      />
      <div className="flex gap-8 max-w-7xl mx-auto">
        {/* Main Article Content */}
        <div 
          className="flex-1 max-w-4xl"
          dir={selectedLanguage === 'ar' ? 'rtl' : 'ltr'}
          lang={selectedLanguage}
        >
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }}
            className="mb-8"
          >

            <div className="mb-4 flex flex-wrap items-center gap-2">
              {article.category === "Press Release" ? (
                <Link to="/press-releases">
                  <Badge className="bg-byteline-green text-byteline-dark hover:bg-byteline-green/90">
                    <T k="auto.press-release-5027" />
                  </Badge>
                </Link>
              ) : (
                <Badge className="bg-byteline-green text-byteline-dark hover:bg-byteline-green/90">
                  {displayCategory}
                </Badge>
              )}
            </div>
            
            {/* Mobile play button moved to image overlay */}
            
            {isTranslating && selectedLanguage !== 'en' && !translatedArticle ? (
              <div className="mb-6 space-y-3">
                <div className="h-9 md:h-12 w-11/12 rounded bg-muted animate-pulse" />
                <div className="h-9 md:h-12 w-3/5 rounded bg-muted animate-pulse" />
              </div>
            ) : (
              <h1
                className="text-3xl md:text-4xl lg:text-5xl font-display font-bold mb-6 leading-tight text-balance"
                dir={selectedLanguage === 'ar' ? 'rtl' : 'ltr'}
              >
                {translatedArticle?.title || article.title}
              </h1>
            )}
            
            <div className="flex flex-wrap items-center justify-between gap-4 mb-8 pb-6 border-b border-border/60">
              <div className="flex items-center">
                {article.author?.avatar && (
                  <SmartImage
                    src={article.author.avatar}
                    alt={article.author.name || 'Author'}
                    w={48}
                    h={48}
                    className="w-12 h-12 rounded-full mr-4 object-cover border-2 border-border"
                  />
                )}
                <div>
                  <p className="font-medium text-lg">
                    <Link to={`/authors/${bylineSlug}`} className="hover:underline">
                      {bylineName}
                    </Link>
                  </p>
                  <p className="text-sm text-muted-foreground">
                    {formattedDate} · {article.readTime}
                    {isOurStory ? '' : ` · ${article.source.name}`}
                  </p>
                </div>
              </div>
              
              <div className="flex items-center space-x-3">
                <Button
                  variant="outline" 
                  size="sm" 
                  className={`transition-colors ${isBookmarked ? "text-byteline-green border-byteline-green" : ""}`}
                  onClick={toggleBookmark}
                >
                  <BookmarkIcon className="mr-2 h-4 w-4" />
                  {isBookmarked ? "Saved" : "Save"}
                </Button>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={() => setShowShareModal(true)}
                >
                  <Share2 className="mr-2 h-4 w-4" />
                  <T k="auto.share-5a95" />
                </Button>
              </div>
            </div>
          </motion.div>
          
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            {article.imageUrl && (
              <div className="mb-10 rounded-xl overflow-hidden shadow-lg">
                <div className="aspect-square md:aspect-video">
                  <SmartImage
                    src={article.imageUrl}
                    alt={article.title}
                    w={800}
                    h={450}
                    priority
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
            )}
            
            {/* Sentiment Analysis */}
            <div className="mb-6">
              <SentimentWidget
                data={sentimentData}
                loading={loadingSentiment}
              />
            </div>
            
            <div className="mb-12">
              {createArticleContent()}
            </div>

            <ArticleFaq articleId={article.id} onItems={setFaqItems} />

            <div id="comments" className="border-t border-border pt-8 mt-12">
              <CommentSection articleId={article.id} />
            </div>
          </motion.div>
          
          {relatedArticles.length > 0 && (
            <div className="border-t border-border pt-12 mt-16">
              <h3 className="text-2xl font-display font-semibold mb-8"><T k="auto.related-articles-cfd2" /></h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {relatedArticles.map((related) => (
                  <ArticleCard key={related.id} article={related} />
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Right Sidebar - Desktop Only */}
        <div className="hidden lg:block w-80 space-y-4 sticky top-4 self-start">
          {/* Action Buttons Row */}
          <div className="flex gap-2 h-12">
            {/* View Source */}
            {article.url && !isOurStory && (
              <a 
                href={article.url} 
                target="_blank" 
                rel="noopener noreferrer"
                className="flex-[2]"
              >
                <div className="bg-card border border-border rounded-lg p-3 hover:border-byteline-green transition-colors h-full flex items-center gap-2">
                  <img 
                    src={`https://www.google.com/s2/favicons?domain=${new URL(article.source.url || article.url).hostname}&sz=64`}
                    alt={article.source.name}
                    className="h-4 w-4 rounded object-cover flex-shrink-0"
                    onError={(e) => {
                      (e.target as HTMLImageElement).style.display = 'none';
                    }}
                  />
                  <span className="text-sm font-medium"><T k="auto.source-6da1" /></span>
                  <ExternalLink className="h-3 w-3 text-muted-foreground flex-shrink-0 ml-auto" />
                </div>
              </a>
            )}
            
            {/* Language Selector */}
            <LanguageSelector
              value={selectedLanguage}
              onChange={onLanguageChange}
              disabled={isTranslating}
            />
            
            {/* Read Article Button */}
            <button
              onClick={isPlaying ? handlePauseArticle : handleReadArticle}
              disabled={isLoadingAudio}
              className={`flex-1 bg-card border border-border rounded-lg p-3 hover:border-byteline-green transition-colors h-full flex items-center justify-center ${isPlaying ? 'text-byteline-green' : ''} disabled:opacity-50 disabled:cursor-not-allowed`}
            >
              {isLoadingAudio ? (
                <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
              ) : isPlaying ? (
                <Pause className="h-4 w-4" />
              ) : (
                <Play className="h-4 w-4" />
              )}
            </button>
          </div>
          
          {/* AI Widgets */}
          <AIWidgetsSidebar 
            tldrData={tldrData}
            loadingTldr={loadingTldr}
            selectedLanguage={selectedLanguage}
          />
        </div>
      </div>

      {/* Floating AI Button - Mobile Only */}
      <FloatingAIButton
        tldrData={tldrData}
        sentimentData={sentimentData}
        loadingTldr={loadingTldr}
        loadingSentiment={loadingSentiment}
      />
      
      {/* Share Article Modal */}
      <ShareArticleModal
        articleId={article.id}
        articleTitle={translatedArticle?.title || article.title}
        isOpen={showShareModal}
        onClose={() => setShowShareModal(false)}
      />
    </>
  );
};

const ArticleSkeleton = () => (
  <div className="max-w-4xl mx-auto">
    <div className="mb-8">
      <Skeleton className="h-6 w-24 mb-4" />
      <Skeleton className="h-12 w-full mb-4" />
      <Skeleton className="h-12 w-3/4 mb-6" />
      
      <div className="flex justify-between items-center mb-8">
        <div className="flex items-center">
          <Skeleton className="w-10 h-10 rounded-full mr-3" />
          <div>
            <Skeleton className="h-5 w-32 mb-1" />
            <Skeleton className="h-4 w-48" />
          </div>
        </div>
        <div className="flex space-x-2">
          <Skeleton className="h-9 w-24" />
          <Skeleton className="h-9 w-24" />
        </div>
      </div>
    </div>
    
    <Skeleton className="w-full h-[400px] rounded-lg mb-8" />
    
    <div className="space-y-4 mb-12">
      <Skeleton className="h-6 w-full" />
      <Skeleton className="h-6 w-full" />
      <Skeleton className="h-6 w-2/3" />
      <Skeleton className="h-6 w-full" />
      <Skeleton className="h-6 w-full" />
      <Skeleton className="h-6 w-4/5" />
      <Skeleton className="h-6 w-full" />
      <Skeleton className="h-6 w-full" />
      <Skeleton className="h-6 w-3/4" />
    </div>
  </div>
);

export default Article;
